/**
 * Calendar popover for picking a static date.
 *
 * Used by:
 *   - "Pick a date…" in the @ mention menu
 *   - clicking an existing GapDate chip to edit it
 *   - the / slash menu's "Date" item
 */

import Box from '@mui/material/Box'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { themeConfig } from '@gap/shared/theme'
import { format } from 'date-fns'
import PopoverSurface, { PopoverPosition } from './PopoverSurface'
import DateUtils from '@gap/utils/date-utils'
import useStore from 'reusable/mobx-store-utils/useStore'

interface Props {
   anchor: PopoverPosition
   onSelect: (iso: string) => void
   onClose: () => void
}

interface QuickOptionProps {
   label: string
   /** Days from today (0 = today, 1 = tomorrow). */
   offsetInDays: number
   onPick: (offsetInDays: number) => void
}

function QuickOption(props: QuickOptionProps) {
   const QuickOptionStore = useStore(sp => ({
      handleMouseDown: (event: React.MouseEvent) => {
         event.preventDefault()
         sp.onPick(sp.offsetInDays)
      }
   }), props)

   return <Box
      role="option"
      onMouseDown={QuickOptionStore.handleMouseDown}
      sx={{
         padding: '6px 8px',
         fontSize: 14,
         borderRadius: '4px',
         cursor: 'pointer',
         color: themeConfig.primary[800],
         '&:hover': { background: themeConfig.light.action.selected },
      }}
   >
      {props.label}
   </Box>
}

const DateSubmenuPopover = observer(function DateSubmenuPopover(props: Props) {
   const dateSubmenuPopoverStore = useStore(sp => ({
      pickQuickOption: (offsetInDays: number) => {
         sp.onSelect(DateUtils.todayIso(offsetInDays))
      },
      handleCalendarChange: (date: Date | null) => {
         if (!date) return
         sp.onSelect(format(date, DateUtils.DATE_FORMAT))
      },
      handleKeyDown: (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation()
            sp.onClose()
         }
      }
   }), props)

   useEffect(() => {
      window.addEventListener('keydown', dateSubmenuPopoverStore.handleKeyDown, true)
      return () => window.removeEventListener('keydown', dateSubmenuPopoverStore.handleKeyDown, true)
   }, [dateSubmenuPopoverStore.handleKeyDown])

   return <PopoverSurface anchor={props.anchor} width={296} maxHeight={420} onDismiss={props.onClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px 8px 8px' }}>
         <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <QuickOption label="Today" offsetInDays={0} onPick={dateSubmenuPopoverStore.pickQuickOption} />
            <QuickOption label="Tomorrow" offsetInDays={1} onPick={dateSubmenuPopoverStore.pickQuickOption} />
         </Box>
         <Box sx={{ height: 1, background: themeConfig.primary[200], margin: '4px 8px' }} />
         <DateCalendar
            onChange={dateSubmenuPopoverStore.handleCalendarChange}
            sx={{
               width: '100%',
               margin: 0,
               '& .MuiPickersCalendarHeader-root': { pl: '12px', pr: '8px', mt: 0 },
            }}
         />
      </Box>
   </PopoverSurface>
})

export default DateSubmenuPopover
